import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'
import { sendEmail } from '../emailService'
import { sendWhatsApp } from '../whatsappClient'
import { checkTeacherAvailability } from '../availabilityHelper'
import { RESCHEDULES_PER_MONTH, noticeMinutes, minutesBefore, usedThisMonth, classStudents, policyWarnings, logReschedule } from '../rescheduleRules'

const warnBox = { marginTop:'10px', padding:'9px 12px', background:'rgba(244,163,53,0.1)', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'8px', fontSize:'12px', color:'#f4a335', lineHeight:1.5 }

const STATUS_STYLE = {
  pending:   { bg:'rgba(244,163,53,0.12)',  color:'#f4a335', label:'Pending'   },
  approved:  { bg:'rgba(16,185,129,0.12)',  color:'#34d399', label:'Approved'  },
  rejected:  { bg:'rgba(239,68,68,0.12)',   color:'#f87171', label:'Rejected'  },
  cancelled: { bg:'rgba(100,116,139,0.12)', color:'#94a3b8', label:'Cancelled' },
}

function fmtDate(d) {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'short', day:'numeric', month:'short', year:'numeric' })
}
function fmtTime(t) {
  if (!t) return '—'
  return new Date('2000-01-01T' + t).toLocaleTimeString('en-IN', { hour:'2-digit', minute:'2-digit', hour12:true })
}

// ── Request Reschedule Modal (Student) ───────────────────────
function RequestRescheduleModal({ cls, profile, onClose, onSave }) {
  const [proposedDate, setProposedDate] = useState('')
  const [proposedTime, setProposedTime] = useState(cls.start_time || '')
  const [reason,       setReason]       = useState('')
  const [saving,       setSaving]       = useState(false)
  const [err,          setErr]          = useState('')
  const [policyNote,   setPolicyNote]   = useState([])

  useEffect(() => {
    (async () => {
      const [students, used] = await Promise.all([classStudents(cls.id), usedThisMonth([profile.id])])
      const notes = []
      const need = noticeMinutes(students.length)
      const left = minutesBefore(cls.class_date, cls.start_time)
      if (left != null && left < need) notes.push(`This class starts in ${left <= 0 ? 'less than a minute' : left + ' minutes'}. Reschedules need ${need === 60 ? '1 hour' : '20 minutes'} notice, so this class may count as taken.`)
      if ((used[profile.id] || 0) >= RESCHEDULES_PER_MONTH) notes.push(`You have already used this month's reschedule (${RESCHEDULES_PER_MONTH} per month). Another one may count as a class taken.`)
      setPolicyNote(notes)
    })().catch(() => {})
  }, [cls.id, cls.class_date, cls.start_time, profile.id])

  async function submit(e) {
    e.preventDefault()
    if (!proposedDate) return setErr('Please select a proposed date.')
    if (!reason.trim()) return setErr('Please give a reason.')
    setSaving(true); setErr('')
    const { error } = await supabase.from('reschedule_requests').insert({
      class_id:       cls.id,
      class_title:    cls.title,
      original_date:  cls.class_date,
      original_time:  cls.start_time || null,
      requested_by:   profile.id,
      requester_name: profile.full_name,
      requester_role: 'student',
      proposed_date:  proposedDate,
      proposed_time:  proposedTime || null,
      reason:         reason.trim(),
      status:         'pending',
    })
    if (error) { setErr(error.message); setSaving(false); return }

    // Notify teacher via email if we can find them
    if (cls.teacher_id) {
      const { data: teacher } = await supabase.from('profiles').select('email,full_name').eq('id', cls.teacher_id).single()
      if (teacher?.email) {
        sendEmail('class_scheduled', teacher.email, {
          name:        teacher.full_name,
          classTitle:  `Reschedule Request: ${cls.title}`,
          classDate:   `${fmtDate(cls.class_date)} → ${fmtDate(proposedDate)}`,
          startTime:   proposedTime || null,
          teacherName: profile.full_name,
          zoomLink:    null,
        })
      }
    }
    onSave()
  }

  const inp = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px 13px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'5px', marginTop:'12px' }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div style={{ background:'var(--bg-page)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'16px', padding:'24px', width:'100%', maxWidth:'460px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <div style={{ fontSize:'15px', fontWeight:800, color:'var(--fg-solid)' }}>📅 Request Reschedule</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'20px', cursor:'pointer' }}>×</button>
        </div>

        {/* Current class info */}
        <div style={{ padding:'12px', background:'rgba(var(--fg-rgb),0.04)', borderRadius:'10px', marginBottom:'4px' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))', marginBottom:'4px' }}>{cls.title}</div>
          <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))' }}>
            📅 {fmtDate(cls.class_date)} {cls.start_time && `· ⏰ ${fmtTime(cls.start_time)}`}
          </div>
        </div>

        <form onSubmit={submit}>
          <label style={lbl}>Proposed New Date *</label>
          <input style={inp} type="date" value={proposedDate} onChange={e=>setProposedDate(e.target.value)}
            min={new Date().toISOString().slice(0,10)} required />

          <label style={lbl}>Proposed New Time</label>
          <input style={inp} type="time" value={proposedTime} onChange={e=>setProposedTime(e.target.value)} />

          <label style={lbl}>Reason for Reschedule *</label>
          <textarea style={{ ...inp, minHeight:'80px', resize:'vertical' }} value={reason}
            onChange={e=>setReason(e.target.value)} placeholder="e.g. I have an exam on this day..." required />

          <div style={{ marginTop:'10px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))', lineHeight:1.5 }}>
            Policy: {RESCHEDULES_PER_MONTH} reschedule per month, asked at least 1 hour before (group/buddy) or 20 minutes before (individual). <a href="https://uniedd.com/policies" target="_blank" rel="noreferrer" style={{ color:'#5aabff' }}>Read the policy</a>
          </div>
          {policyNote.map(n => <div key={n} style={warnBox}>⚠ {n}</div>)}

          {err && <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}

          <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
            <button type="submit" disabled={saving} style={{ flex:1, padding:'11px', background:'linear-gradient(135deg,#1e90ff,#0ea5e9)', color:'#fff', fontSize:'13px', fontWeight:800, border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>
              {saving ? 'Submitting...' : '📤 Submit Request'}
            </button>
            <button type="button" onClick={onClose} style={{ padding:'11px 18px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Teacher: Cancel Class Modal ──────────────────────────────
function CancelClassModal({ cls, profile, onClose, onSave }) {
  const [reason,  setReason]  = useState('')
  const [saving,  setSaving]  = useState(false)
  const [err,     setErr]     = useState('')

  async function submit(e) {
    e.preventDefault()
    if (!reason.trim()) return setErr('Please give a reason.')
    setSaving(true)
    const { error } = await supabase.from('classes').update({
      is_cancelled:  true,
      cancelled_by:  profile.id,
      cancelled_at:  new Date().toISOString(),
      cancel_reason: reason.trim(),
    }).eq('id', cls.id)
    if (error) { setErr(error.message); setSaving(false); return }

    // Notify enrolled students
    const { data: enrollments } = await supabase
      .from('enrollments').select('student_id, profiles:student_id(email,full_name,phone,timezone)')
      .eq('class_id', cls.id)
    for (const e of (enrollments || [])) {
      if (e.profiles?.email) {
        sendEmail('class_scheduled', e.profiles.email, {
          name:        e.profiles.full_name,
          classTitle:  `CANCELLED: ${cls.title}`,
          classDate:   fmtDate(cls.class_date),
          startTime:   cls.start_time || null,
          teacherName: profile.full_name,
          zoomLink:    null,
          studentTZ:   e.profiles.timezone,
        })
      }
      if (e.profiles?.phone) {
        // class_cancelled_wa is POSITIONAL — order: name, class_name, date_time, reason.
        sendWhatsApp('class_cancelled_wa', e.profiles.phone, [
          e.profiles.full_name,
          cls.title,
          `${fmtDate(cls.class_date)}${cls.start_time ? ' at ' + fmtTime(cls.start_time) : ''}`,
          reason.trim(),
        ], { classId: cls.id, recipientId: e.student_id })
      }
    }

    // Notify the class's teacher too (in case an admin cancelled on their behalf)
    if (cls.teacher_id) {
      const { data: teacher } = await supabase.from('profiles').select('email,full_name,phone,timezone').eq('id', cls.teacher_id).single()
      if (teacher?.email) {
        sendEmail('class_scheduled', teacher.email, {
          name:        teacher.full_name,
          classTitle:  `CANCELLED: ${cls.title}`,
          classDate:   fmtDate(cls.class_date),
          startTime:   cls.start_time || null,
          teacherName: profile.full_name,
          zoomLink:    null,
          studentTZ:   teacher.timezone,
        })
      }
      if (teacher?.phone) {
        sendWhatsApp('class_cancelled_wa', teacher.phone, [
          teacher.full_name,
          cls.title,
          `${fmtDate(cls.class_date)}${cls.start_time ? ' at ' + fmtTime(cls.start_time) : ''}`,
          reason.trim(),
        ], { classId: cls.id, recipientId: cls.teacher_id })
      }
    }
    onSave()
  }

  const inp = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px 13px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'5px', marginTop:'12px' }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div style={{ background:'var(--bg-page)', border:'0.5px solid rgba(239,68,68,0.3)', borderRadius:'16px', padding:'24px', width:'100%', maxWidth:'420px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <div style={{ fontSize:'15px', fontWeight:800, color:'#f87171' }}>🚫 Cancel Class</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'20px', cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:'12px', background:'rgba(239,68,68,0.08)', borderRadius:'10px', marginBottom:'12px', border:'0.5px solid rgba(239,68,68,0.2)' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>{cls.title}</div>
          <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'3px' }}>{fmtDate(cls.class_date)} {cls.start_time && `· ${fmtTime(cls.start_time)}`}</div>
        </div>
        <form onSubmit={submit}>
          <label style={lbl}>Reason for Cancellation *</label>
          <textarea style={{ ...inp, minHeight:'80px', resize:'vertical' }} value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Teacher unwell, emergency, holiday..." required />
          {err && <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}
          <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
            <button type="submit" disabled={saving} style={{ flex:1, padding:'11px', background:'rgba(239,68,68,0.8)', color:'#fff', fontSize:'13px', fontWeight:800, border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>
              {saving ? 'Cancelling...' : '🚫 Confirm Cancel'}
            </button>
            <button type="button" onClick={onClose} style={{ padding:'11px 18px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>Back</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Teacher: Reschedule Class Directly ───────────────────────
function TeacherRescheduleModal({ cls, profile, onClose, onSave }) {
  const [newDate, setNewDate] = useState(cls.class_date || '')
  const [newTime, setNewTime] = useState(cls.start_time || '')
  const [reason,  setReason]  = useState('')
  const [saving,  setSaving]  = useState(false)
  const [err,     setErr]     = useState('')
  const [availWarning,      setAvailWarning]      = useState('')
  const [confirmedOverride, setConfirmedOverride] = useState(false)
  const [students,  setStudents]  = useState([])
  const [used,      setUsed]      = useState({})
  const [askedBy,   setAskedBy]   = useState('student')   // 'student' = student/parent asked (counts towards their 1 a month)
  const [whoAsked,  setWhoAsked]  = useState('')          // which student, in a group class

  useEffect(() => { setAvailWarning(''); setConfirmedOverride(false) }, [newDate, newTime])
  useEffect(() => {
    (async () => {
      const list = await classStudents(cls.id)
      setStudents(list); setWhoAsked(list[0]?.id || '')
      setUsed(await usedThisMonth(list.map(x => x.id)))
    })().catch(() => {})
  }, [cls.id])

  const askingStudents = askedBy === 'student' ? students.filter(x => x.id === whoAsked) : []
  const policyWarns = askedBy === 'student'
    ? policyWarnings({ cls, studentCount: students.length, students: askingStudents, used })
    : []

  async function submit(e, overriding = false) {
    e?.preventDefault()
    if (!newDate) return setErr('Select new date.')
    setErr('')

    if (cls.teacher_id && newTime && !confirmedOverride && !overriding) {
      const check = await checkTeacherAvailability({ teacherId: cls.teacher_id, date: newDate, startTime: newTime, durationMinutes: cls.duration_minutes, excludeClassId: cls.id })
      if (check.conflicts.length > 0) return setErr(`⛔ ${check.reason}`)
      if (!check.withinAvailability) { setAvailWarning(check.reason); return }
    }
    if (overriding) setConfirmedOverride(true)

    setSaving(true)
    const { error } = await supabase.from('classes').update({
      class_date:             newDate,
      start_time:             newTime || null,
      rescheduled_from_date:  cls.class_date,
      rescheduled_from_time:  cls.start_time || null,
    }).eq('id', cls.id)
    if (error) { setErr(error.message); setSaving(false); return }

    const need = noticeMinutes(students.length)
    const left = minutesBefore(cls.class_date, cls.start_time)
    await logReschedule(askingStudents.map(st => ({
      student_id: st.id, class_id: cls.id, class_title: cls.title,
      from_date: cls.class_date, from_time: cls.start_time || null, to_date: newDate, to_time: newTime || null,
      asked_by: 'student', late: left != null && left < need, over_limit: (used[st.id] || 0) >= RESCHEDULES_PER_MONTH,
    })))

    // Update events table too
    await supabase.from('events').update({
      day:   new Date(newDate + 'T00:00:00').getDate(),
      month: new Date(newDate + 'T00:00:00').getMonth() + 1,
      year:  new Date(newDate + 'T00:00:00').getFullYear(),
      time:  newTime || null,
    }).eq('title', cls.title)

    // Notify students
    const { data: enrollments } = await supabase
      .from('enrollments').select('student_id, profiles:student_id(email,full_name,phone,timezone)')
      .eq('class_id', cls.id)
    for (const enr of (enrollments || [])) {
      if (enr.profiles?.email) {
        sendEmail('class_scheduled', enr.profiles.email, {
          name:        enr.profiles.full_name,
          classTitle:  `RESCHEDULED: ${cls.title}`,
          classDate:   newDate,
          startTime:   newTime || null,
          teacherName: profile.full_name,
          zoomLink:    cls.meet_link || null,
          studentTZ:   enr.profiles.timezone,
        })
      }
      if (enr.profiles?.phone) {
        // class_rescheduled_wa is POSITIONAL — order: name, class_name, old_date_time, new_date_time.
        sendWhatsApp('class_rescheduled_wa', enr.profiles.phone, [
          enr.profiles.full_name,
          cls.title,
          `${fmtDate(cls.class_date)}${cls.start_time ? ' at ' + fmtTime(cls.start_time) : ''}`,
          `${fmtDate(newDate)}${newTime ? ' at ' + fmtTime(newTime) : ''}`,
        ], { classId: cls.id, recipientId: enr.student_id })
      }
    }

    // Notify the class's teacher too (in case an admin rescheduled on their behalf)
    if (cls.teacher_id) {
      const { data: teacher } = await supabase.from('profiles').select('email,full_name,phone,timezone').eq('id', cls.teacher_id).single()
      if (teacher?.email) {
        sendEmail('class_scheduled', teacher.email, {
          name:        teacher.full_name,
          classTitle:  `RESCHEDULED: ${cls.title}`,
          classDate:   newDate,
          startTime:   newTime || null,
          teacherName: profile.full_name,
          zoomLink:    cls.meet_link || null,
          studentTZ:   teacher.timezone,
        })
      }
      if (teacher?.phone) {
        sendWhatsApp('class_rescheduled_wa', teacher.phone, [
          teacher.full_name,
          cls.title,
          `${fmtDate(cls.class_date)}${cls.start_time ? ' at ' + fmtTime(cls.start_time) : ''}`,
          `${fmtDate(newDate)}${newTime ? ' at ' + fmtTime(newTime) : ''}`,
        ], { classId: cls.id, recipientId: cls.teacher_id })
      }
    }
    onSave()
  }

  const inp = { width:'100%', background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'10px 13px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark', boxSizing:'border-box' }
  const lbl = { display:'block', fontSize:'10px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a30))', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:'5px', marginTop:'12px' }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' }}>
      <div style={{ background:'var(--bg-page)', border:'0.5px solid rgba(139,92,246,0.3)', borderRadius:'16px', padding:'24px', width:'100%', maxWidth:'440px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <div style={{ fontSize:'15px', fontWeight:800, color:'#a78bfa' }}>📅 Reschedule Class</div>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'rgba(var(--fg-rgb),var(--a40))', fontSize:'20px', cursor:'pointer' }}>×</button>
        </div>
        <div style={{ padding:'12px', background:'rgba(139,92,246,0.08)', borderRadius:'10px', marginBottom:'4px', border:'0.5px solid rgba(139,92,246,0.2)' }}>
          <div style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a80))' }}>{cls.title}</div>
          <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginTop:'3px' }}>Currently: {fmtDate(cls.class_date)} {cls.start_time && `· ${fmtTime(cls.start_time)}`}</div>
        </div>
        <form onSubmit={submit}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
            <div>
              <label style={lbl}>New Date *</label>
              <input style={inp} type="date" value={newDate} onChange={e=>setNewDate(e.target.value)} required />
            </div>
            <div>
              <label style={lbl}>New Time</label>
              <input style={inp} type="time" value={newTime} onChange={e=>setNewTime(e.target.value)} />
            </div>
          </div>
          <label style={lbl}>Reason (optional)</label>
          <input style={inp} type="text" value={reason} onChange={e=>setReason(e.target.value)} placeholder="e.g. Holiday, personal adjustment..." />
          <label style={lbl}>Who asked for this?</label>
          <div style={{ display:'grid', gridTemplateColumns: askedBy === 'student' && students.length > 1 ? '1fr 1fr' : '1fr', gap:'10px' }}>
            <select style={inp} value={askedBy} onChange={e=>setAskedBy(e.target.value)}>
              <option value="student">Student / parent (counts as their reschedule)</option>
              <option value="teacher">Teacher / UniEDD (doesn't count)</option>
            </select>
            {askedBy === 'student' && students.length > 1 && (
              <select style={inp} value={whoAsked} onChange={e=>setWhoAsked(e.target.value)}>
                {students.map(st => <option key={st.id} value={st.id}>{st.full_name}</option>)}
              </select>
            )}
          </div>
          {askedBy === 'student' && askingStudents.length > 0 && policyWarns.length === 0 && (
            <div style={{ marginTop:'8px', fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>
              ✓ Within policy: {askingStudents[0].full_name} has used {used[askingStudents[0].id] || 0} of {RESCHEDULES_PER_MONTH} reschedule this month.
            </div>
          )}
          {policyWarns.map(w => <div key={w} style={warnBox}>⚠ {w} You can still reschedule if you choose to.</div>)}
          {availWarning && (
            <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(244,163,53,0.1)', border:'0.5px solid rgba(244,163,53,0.3)', borderRadius:'8px', fontSize:'12px', color:'#f4a335' }}>
              ⚠ {availWarning}. <button type="button" disabled={saving} onClick={() => submit(null, true)}
                style={{ background:'none', border:'none', color:'var(--fg-solid)', fontWeight:800, textDecoration:'underline', cursor:saving?'not-allowed':'pointer', fontFamily:'inherit', fontSize:'12px', padding:0 }}>
                Reschedule anyway →
              </button>
            </div>
          )}
          {err && <div style={{ marginTop:'8px', padding:'8px 12px', background:'rgba(239,68,68,0.1)', color:'#f87171', borderRadius:'8px', fontSize:'12px' }}>{err}</div>}
          <div style={{ display:'flex', gap:'8px', marginTop:'16px' }}>
            <button type="submit" disabled={saving} style={{ flex:1, padding:'11px', background:'linear-gradient(135deg,#8b5cf6,#6d28d9)', color:'#fff', fontSize:'13px', fontWeight:800, border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>
              {saving ? 'Saving...' : '✓ Confirm Reschedule'}
            </button>
            <button type="button" onClick={onClose} style={{ padding:'11px 18px', background:'rgba(var(--fg-rgb),0.06)', color:'rgba(var(--fg-rgb),var(--a50))', border:'none', borderRadius:'10px', cursor:'pointer', fontFamily:'inherit' }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Main RescheduleManager Component ────────────────────────
export default function RescheduleManager({ profile }) {
  const [classes,   setClasses]   = useState([])
  const [requests,  setRequests]  = useState([])
  const [loading,   setLoading]   = useState(true)
  const [tab,       setTab]       = useState('classes')
  const [search,    setSearch]    = useState('')
  const [modal,     setModal]     = useState(null) // {type:'cancel'|'reschedule'|'request', cls}
  const [ok,        setOk]        = useState('')

  const isTeacher = profile.role === 'teacher'
  const isStudent = profile.role === 'student'
  const isAdmin   = profile.role === 'admin'

  const load = useCallback(async () => {
    setLoading(true)
    // India date: the UTC date lags until 5:30 am IST and showed yesterday's classes.
    const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })

    // Load classes
    let clsQ = supabase.from('classes').select('*')
      .eq('is_cancelled', false)
      .gte('class_date', today)
      .order('class_date')
    if (isTeacher) clsQ = clsQ.eq('teacher_id', profile.id)
    else if (isStudent) {
      const { data: enrols } = await supabase.from('enrollments').select('class_id').eq('student_id', profile.id)
      const ids = (enrols||[]).map(e=>e.class_id).filter(Boolean)
      if (ids.length) clsQ = clsQ.in('id', ids)
      else { setClasses([]); setLoading(false); return }
    }
    const { data: cls } = await clsQ
    setClasses(cls || [])

    // Load reschedule requests
    let reqQ = supabase.from('reschedule_requests').select('*').order('created_at', { ascending:false })
    if (isStudent)      reqQ = reqQ.eq('requested_by', profile.id)
    else if (isTeacher) {
      const classIds = (cls||[]).map(c=>c.id)
      if (classIds.length) reqQ = reqQ.in('class_id', classIds)
    }
    const { data: reqs } = await reqQ
    setRequests(reqs || [])

    setLoading(false)
  }, [profile.id, profile.role, isTeacher, isStudent])

  useEffect(() => { load() }, [load])

  async function approveRequest(req) {
    // Policy check: notice given (when they asked vs the class time) and this month's count.
    const students = await classStudents(req.class_id)
    const used = await usedThisMonth([req.requested_by], { excludeRequestId: req.id })
    const asker = [{ id: req.requested_by, full_name: req.requester_name || 'This student' }]
    const warns = req.requester_role === 'student'
      ? policyWarnings({ cls: { class_date: req.original_date, start_time: req.original_time }, studentCount: students.length, askedAt: req.created_at, students: asker, used })
      : []
    if (warns.length && !window.confirm(`⚠ ${warns.join('\n\n')}\n\nApprove this reschedule anyway?`)) return

    // Availability check — needs the class's teacher fetched up front
    // (previously only fetched later, for the notification email).
    const { data: clsForCheck } = await supabase.from('classes').select('teacher_id,duration_minutes').eq('id', req.class_id).single()
    if (clsForCheck?.teacher_id && req.proposed_time) {
      const check = await checkTeacherAvailability({
        teacherId: clsForCheck.teacher_id, date: req.proposed_date, startTime: req.proposed_time,
        durationMinutes: clsForCheck.duration_minutes, excludeClassId: req.class_id,
      })
      if (check.conflicts.length > 0) {
        window.alert(`⛔ Can't approve — ${check.reason}`)
        return
      }
      if (!check.withinAvailability && !window.confirm(`⚠ ${check.reason}. Approve this reschedule anyway?`)) {
        return
      }
    }

    // Update the actual class date
    await supabase.from('classes').update({
      class_date:             req.proposed_date,
      start_time:             req.proposed_time || null,
      rescheduled_from_date:  req.original_date,
      rescheduled_from_time:  req.original_time || null,
    }).eq('id', req.class_id)

    // Update events table
    await supabase.from('events').update({
      day:   new Date(req.proposed_date + 'T00:00:00').getDate(),
      month: new Date(req.proposed_date + 'T00:00:00').getMonth() + 1,
      year:  new Date(req.proposed_date + 'T00:00:00').getFullYear(),
      time:  req.proposed_time || null,
    }).eq('title', req.class_title)

    if (req.requester_role === 'student') {
      const left = minutesBefore(req.original_date, req.original_time, req.created_at)
      await logReschedule([{
        student_id: req.requested_by, class_id: req.class_id, class_title: req.class_title,
        from_date: req.original_date, from_time: req.original_time || null, to_date: req.proposed_date, to_time: req.proposed_time || null,
        asked_by: 'student', late: left != null && left < noticeMinutes(students.length), over_limit: (used[req.requested_by] || 0) >= RESCHEDULES_PER_MONTH,
      }])
    }

    // Mark request approved
    await supabase.from('reschedule_requests').update({
      status:      'approved',
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
    }).eq('id', req.id)

    // Notify student
    const { data: student } = await supabase.from('profiles').select('email,full_name,phone,timezone').eq('id', req.requested_by).single()
    if (student?.email) {
      sendEmail('class_scheduled', student.email, {
        name:        student.full_name,
        classTitle:  `✅ Reschedule Approved: ${req.class_title}`,
        classDate:   req.proposed_date,
        startTime:   req.proposed_time || null,
        teacherName: profile.full_name,
        zoomLink:    null,
        studentTZ:   student.timezone,
      })
    }
    if (student?.phone) {
      // class_rescheduled_wa is POSITIONAL — order: name, class_name, old_date_time, new_date_time.
      sendWhatsApp('class_rescheduled_wa', student.phone, [
        student.full_name,
        req.class_title,
        `${fmtDate(req.original_date)}${req.original_time ? ' at ' + fmtTime(req.original_time) : ''}`,
        `${fmtDate(req.proposed_date)}${req.proposed_time ? ' at ' + fmtTime(req.proposed_time) : ''}`,
      ], { classId: req.class_id, recipientId: req.requested_by })
    }

    // Notify the class's teacher too (in case an admin approved this, not the teacher themselves)
    const { data: cls } = await supabase.from('classes').select('teacher_id').eq('id', req.class_id).single()
    if (cls?.teacher_id) {
      const { data: teacher } = await supabase.from('profiles').select('email,full_name,phone,timezone').eq('id', cls.teacher_id).single()
      if (teacher?.email) {
        sendEmail('class_scheduled', teacher.email, {
          name:        teacher.full_name,
          classTitle:  `✅ Reschedule Approved: ${req.class_title}`,
          classDate:   req.proposed_date,
          startTime:   req.proposed_time || null,
          teacherName: profile.full_name,
          zoomLink:    null,
          studentTZ:   teacher.timezone,
        })
      }
      if (teacher?.phone) {
        sendWhatsApp('class_rescheduled_wa', teacher.phone, [
          teacher.full_name,
          req.class_title,
          `${fmtDate(req.original_date)}${req.original_time ? ' at ' + fmtTime(req.original_time) : ''}`,
          `${fmtDate(req.proposed_date)}${req.proposed_time ? ' at ' + fmtTime(req.proposed_time) : ''}`,
        ], { classId: req.class_id, recipientId: cls.teacher_id })
      }
    }
    setOk('✓ Request approved — class rescheduled!')
    setTimeout(()=>setOk(''),4000)
    load()
  }

  async function rejectRequest(req) {
    await supabase.from('reschedule_requests').update({
      status:      'rejected',
      reviewed_by: profile.id,
      reviewed_at: new Date().toISOString(),
    }).eq('id', req.id)

    // Notify student
    const { data: student } = await supabase.from('profiles').select('email,full_name').eq('id', req.requested_by).single()
    if (student?.email) {
      sendEmail('class_scheduled', student.email, {
        name:        student.full_name,
        classTitle:  `❌ Reschedule Declined: ${req.class_title}`,
        classDate:   req.original_date,
        startTime:   req.original_time || null,
        teacherName: profile.full_name,
        zoomLink:    null,
      })
    }

    // Notify the class's teacher too (in case an admin rejected this, not the teacher themselves)
    const { data: cls } = await supabase.from('classes').select('teacher_id').eq('id', req.class_id).single()
    if (cls?.teacher_id) {
      const { data: teacher } = await supabase.from('profiles').select('email,full_name').eq('id', cls.teacher_id).single()
      if (teacher?.email) {
        sendEmail('class_scheduled', teacher.email, {
          name:        teacher.full_name,
          classTitle:  `❌ Reschedule Declined: ${req.class_title}`,
          classDate:   req.original_date,
          startTime:   req.original_time || null,
          teacherName: profile.full_name,
          zoomLink:    null,
        })
      }
    }
    setOk('Request rejected.')
    setTimeout(()=>setOk(''),3000)
    load()
  }

  const pendingCount  = requests.filter(r=>r.status==='pending').length
  const filteredCls   = classes.filter(c => !search || c.title?.toLowerCase().includes(search.toLowerCase()))

  const inp = { background:'var(--bg-input)', border:'0.5px solid rgba(var(--fg-rgb),0.1)', borderRadius:'10px', padding:'8px 12px', fontSize:'13px', color:'rgba(var(--fg-rgb),var(--a85))', outline:'none', fontFamily:'inherit', colorScheme:'dark' }

  return (
    <div id="uniedd-reschedule" style={{ marginTop:'14px' }}>
      <div style={{ fontSize:'16px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))', marginBottom:'4px' }}>📅 Reschedule & Cancellations</div>
      <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'14px' }}>
        {isStudent ? 'Request a reschedule for your upcoming classes' :
         isTeacher ? 'Reschedule or cancel your classes, review student requests' :
         'Manage all class reschedule and cancellation requests'}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'6px', marginBottom:'16px', flexWrap:'wrap' }}>
        {[
          { id:'classes',  label:'📋 Upcoming Classes' },
          { id:'requests', label:`🔔 Requests${pendingCount>0?` (${pendingCount})`:''}`  },
          ...(isTeacher||isAdmin ? [{ id:'rescheduled', label:'↺ Rescheduled' }] : []),
          ...(isTeacher||isAdmin ? [{ id:'cancelled', label:'🚫 Cancelled' }] : []),
        ].map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} style={{ fontSize:'12px', fontWeight:700, padding:'7px 16px', borderRadius:'10px', border:'none', cursor:'pointer', fontFamily:'inherit', background:tab===t.id?'linear-gradient(135deg,#1e90ff,#0ea5e9)':'rgba(var(--fg-rgb),0.06)', color:tab===t.id?'#fff':'rgba(var(--fg-rgb),var(--a45))', position:'relative' }}>
            {t.label}
            {t.id==='requests' && pendingCount>0 && (
              <span style={{ position:'absolute', top:'-4px', right:'-4px', width:'16px', height:'16px', borderRadius:'50%', background:'#ef4444', fontSize:'9px', fontWeight:800, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {ok && <div style={{ padding:'8px 14px', background:'rgba(16,185,129,0.1)', border:'0.5px solid rgba(16,185,129,0.25)', borderRadius:'9px', fontSize:'13px', color:'#34d399', marginBottom:'10px' }}>{ok}</div>}

      {loading ? (
        <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
      ) : (
        <>
          {/* ── UPCOMING CLASSES TAB ── */}
          {tab === 'classes' && (
            <>
              <input style={{ ...inp, width:'100%', marginBottom:'12px' }} type="text" placeholder="🔍 Search classes..." value={search} onChange={e=>setSearch(e.target.value)} />
              {filteredCls.length === 0 ? (
                <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No upcoming classes found</div>
              ) : (
                <div style={{ display:'grid', gap:'8px' }}>
                  {filteredCls.map(cls => (
                    <div key={cls.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', border:'0.5px solid rgba(var(--fg-rgb),0.07)' }}>
                      {/* Rescheduled badge */}
                      {cls.rescheduled_from_date && (
                        <div style={{ position:'absolute', fontSize:'8px', color:'#a78bfa' }}>↺</div>
                      )}
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a85))' }}>{cls.title}</div>
                        <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px', display:'flex', gap:'8px', flexWrap:'wrap' }}>
                          <span>📅 {fmtDate(cls.class_date)}</span>
                          {cls.start_time && <span>⏰ {fmtTime(cls.start_time)}</span>}
                          {cls.teacher_name && <span>👨‍🏫 {cls.teacher_name}</span>}
                          {cls.rescheduled_from_date && <span style={{ color:'#a78bfa' }}>↺ Rescheduled</span>}
                        </div>
                      </div>

                      {/* Action buttons by role */}
                      <div style={{ display:'flex', gap:'6px', flexShrink:0, alignItems:'center', flexWrap:'wrap' }}>
                        {/* Join button — always show if meet_link exists */}
                        {cls.meet_link ? (
                          <a href={cls.meet_link} target="_blank" rel="noreferrer"
                            style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.3)', textDecoration:'none', whiteSpace:'nowrap' }}>
                            🔗 Join Class
                          </a>
                        ) : (
                          <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a20))', fontStyle:'italic' }}>No link yet</span>
                        )}

                        {/* Student: can only request */}
                        {isStudent && (
                          <button onClick={()=>setModal({type:'request',cls})} style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', background:'rgba(30,144,255,0.12)', color:'#5aabff', border:'0.5px solid rgba(30,144,255,0.2)', cursor:'pointer', fontFamily:'inherit' }}>
                            📅 Request Reschedule
                          </button>
                        )}

                        {/* Teacher/Admin: can reschedule and cancel directly */}
                        {(isTeacher || isAdmin) && (
                          <>
                            <button onClick={()=>setModal({type:'reschedule',cls})} style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', background:'rgba(139,92,246,0.12)', color:'#a78bfa', border:'0.5px solid rgba(139,92,246,0.2)', cursor:'pointer', fontFamily:'inherit' }}>
                              ↺ Reschedule
                            </button>
                            <button onClick={()=>setModal({type:'cancel',cls})} style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', background:'rgba(239,68,68,0.1)', color:'#f87171', border:'0.5px solid rgba(239,68,68,0.2)', cursor:'pointer', fontFamily:'inherit' }}>
                              🚫 Cancel
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* ── REQUESTS TAB ── */}
          {tab === 'requests' && (
            <>
              {requests.length === 0 ? (
                <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>
                  {isStudent ? 'You haven\'t made any reschedule requests yet.' : 'No reschedule requests from students yet.'}
                </div>
              ) : (
                <div style={{ display:'grid', gap:'10px' }}>
                  {requests.map(req => {
                    const ss = STATUS_STYLE[req.status] || STATUS_STYLE.pending
                    return (
                      <div key={req.id} style={{ background:'rgba(var(--fg-rgb),0.03)', borderRadius:'12px', padding:'14px 16px', border:`0.5px solid ${ss.color}22` }}>
                        <div style={{ display:'flex', alignItems:'flex-start', gap:'12px' }}>
                          <div style={{ flex:1 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'6px', flexWrap:'wrap' }}>
                              <span style={{ fontSize:'13px', fontWeight:700, color:'rgba(var(--fg-rgb),var(--a85))' }}>{req.class_title}</span>
                              <span style={{ fontSize:'10px', fontWeight:700, padding:'2px 8px', borderRadius:'6px', background:ss.bg, color:ss.color }}>{ss.label}</span>
                            </div>
                            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px', marginBottom:'8px' }}>
                              <div style={{ padding:'8px', background:'rgba(239,68,68,0.08)', borderRadius:'8px', border:'0.5px solid rgba(239,68,68,0.15)' }}>
                                <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'2px' }}>ORIGINAL</div>
                                <div style={{ fontSize:'12px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a60))' }}>{fmtDate(req.original_date)}</div>
                                {req.original_time && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a40))' }}>{fmtTime(req.original_time)}</div>}
                              </div>
                              <div style={{ padding:'8px', background:'rgba(16,185,129,0.08)', borderRadius:'8px', border:'0.5px solid rgba(16,185,129,0.15)' }}>
                                <div style={{ fontSize:'9px', color:'rgba(var(--fg-rgb),var(--a30))', marginBottom:'2px' }}>PROPOSED</div>
                                <div style={{ fontSize:'12px', fontWeight:600, color:'#34d399' }}>{fmtDate(req.proposed_date)}</div>
                                {req.proposed_time && <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a50))' }}>{fmtTime(req.proposed_time)}</div>}
                              </div>
                            </div>
                            <div style={{ fontSize:'12px', color:'rgba(var(--fg-rgb),var(--a40))', marginBottom:'6px' }}>
                              <span style={{ color:'rgba(var(--fg-rgb),var(--a50))', fontWeight:600 }}>Reason: </span>{req.reason}
                            </div>
                            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a25))' }}>
                              Requested by {req.requester_name} · {new Date(req.created_at).toLocaleDateString('en-IN')}
                            </div>
                          </div>

                          {/* Teacher/Admin actions on pending requests */}
                          {(isTeacher || isAdmin) && req.status === 'pending' && (
                            <div style={{ display:'flex', flexDirection:'column', gap:'6px', flexShrink:0 }}>
                              <button onClick={()=>approveRequest(req)} style={{ fontSize:'12px', fontWeight:700, padding:'7px 16px', borderRadius:'9px', background:'rgba(16,185,129,0.15)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.25)', cursor:'pointer', fontFamily:'inherit' }}>
                                ✓ Approve
                              </button>
                              <button onClick={()=>rejectRequest(req)} style={{ fontSize:'12px', fontWeight:700, padding:'7px 16px', borderRadius:'9px', background:'rgba(239,68,68,0.1)', color:'#f87171', border:'0.5px solid rgba(239,68,68,0.2)', cursor:'pointer', fontFamily:'inherit' }}>
                                ✕ Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          )}

          {/* ── RESCHEDULED TAB ── */}
          {tab === 'rescheduled' && (isTeacher || isAdmin) && (
            <RescheduledClasses profile={profile} />
          )}

          {/* ── CANCELLED TAB ── */}
          {tab === 'cancelled' && (isTeacher || isAdmin) && (
            <CancelledClasses profile={profile} />
          )}
        </>
      )}

      {/* Modals */}
      {modal?.type === 'request'    && <RequestRescheduleModal    cls={modal.cls} profile={profile} onClose={()=>setModal(null)} onSave={()=>{setModal(null);setOk('✓ Request submitted — teacher will review it.');load()}} />}
      {modal?.type === 'cancel'     && <CancelClassModal          cls={modal.cls} profile={profile} onClose={()=>setModal(null)} onSave={()=>{setModal(null);setOk('✓ Class cancelled — students notified.');load()}} />}
      {modal?.type === 'reschedule' && <TeacherRescheduleModal    cls={modal.cls} profile={profile} onClose={()=>setModal(null)} onSave={()=>{setModal(null);setOk('✓ Class rescheduled — students notified.');load()}} />}
    </div>
  )
}

// ── Rescheduled classes list ──────────────────────────────────
function RescheduledClasses({ profile }) {
  const [rescheduled, setRescheduled] = useState([])
  const [loading,      setLoading]     = useState(true)

  useEffect(() => {
    async function load() {
      let q = supabase.from('classes').select('*').not('rescheduled_from_date', 'is', null).order('class_date', { ascending:false })
      if (profile.role === 'teacher') q = q.eq('teacher_id', profile.id)
      const { data } = await q
      setRescheduled(data || [])
      setLoading(false)
    }
    load()
  }, [profile.id, profile.role])

  if (loading) return <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
  if (!rescheduled.length) return <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>No rescheduled classes.</div>

  return (
    <div style={{ display:'grid', gap:'8px' }}>
      {rescheduled.map(cls => (
        <div key={cls.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'rgba(139,92,246,0.05)', borderRadius:'12px', border:'0.5px solid rgba(139,92,246,0.15)' }}>
          <span style={{ fontSize:'18px' }}>↺</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a80))' }}>{cls.title}</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a35))', marginTop:'2px' }}>
              Was: {fmtDate(cls.rescheduled_from_date)} {cls.rescheduled_from_time && `· ${fmtTime(cls.rescheduled_from_time)}`}
              {' → '}Now: {fmtDate(cls.class_date)} {cls.start_time && `· ${fmtTime(cls.start_time)}`}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// Class start (stored as India time) shown in the viewer's own time zone.
function classTimeLabel(cls, tz) {
  const start = new Date(`${cls.class_date}T${(cls.start_time || '00:00').slice(0, 5)}:00+05:30`)
  try {
    return start.toLocaleString('en-IN', { weekday:'short', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit', hour12:true, timeZone: tz || 'Asia/Kolkata' }) + (tz ? ' your time' : ' IST')
  } catch {
    return `${fmtDate(cls.class_date)} ${fmtTime(cls.start_time)} IST`
  }
}

// ── Cancelled classes list ────────────────────────────────────
function CancelledClasses({ profile }) {
  const [cancelled, setCancelled] = useState([])
  const [loading,   setLoading]   = useState(true)
  const [busyId,    setBusyId]    = useState(null)
  const [msg,       setMsg]       = useState('')

  const load = useCallback(async () => {
    let q = supabase.from('classes').select('*').eq('is_cancelled', true).order('cancelled_at', { ascending:false })
    if (profile.role === 'teacher') q = q.eq('teacher_id', profile.id)
    const { data } = await q
    setCancelled(data || [])
    setLoading(false)
  }, [profile.id, profile.role])

  useEffect(() => { load() }, [load])

  // Un-cancels a class cancelled by mistake, then tells the student(s) and
  // teacher it's back on (class_reminder_1h, which carries the joining link).
  // The usual 1-hour reminder and the calendar invite follow automatically.
  async function undoCancel(cls) {
    if (!window.confirm(`Bring back "${cls.title}" on ${fmtDate(cls.class_date)}${cls.start_time ? ' at ' + fmtTime(cls.start_time) : ''}?\n\nThe student and teacher get a WhatsApp and email that the class is on.`)) return
    setBusyId(cls.id); setMsg('')
    if (cls.teacher_id && cls.start_time) {
      const check = await checkTeacherAvailability({ teacherId: cls.teacher_id, date: cls.class_date, startTime: cls.start_time, durationMinutes: cls.duration_minutes, excludeClassId: cls.id })
      if (check.conflicts.length > 0) { setMsg(`⛔ Can't bring it back: ${check.reason}. Reschedule the other class first.`); setBusyId(null); return }
    }
    const { error } = await supabase.from('classes').update({
      is_cancelled: false, cancel_reason: null, cancelled_at: null, cancelled_by: null, hard_delete_at: null,
      reminder_sent_at: null, email_reminder_sent_at: null,
    }).eq('id', cls.id)
    if (error) { setMsg(`⛔ ${error.message}`); setBusyId(null); return }

    const { data: enrollments } = await supabase
      .from('enrollments').select('student_id, profiles:student_id(email,full_name,phone,timezone)')
      .eq('class_id', cls.id)
    const people = (enrollments || []).filter(e => e.profiles).map(e => ({ id: e.student_id, ...e.profiles }))
    if (cls.teacher_id) {
      const { data: teacher } = await supabase.from('profiles').select('email,full_name,phone,timezone').eq('id', cls.teacher_id).single()
      if (teacher) people.push({ id: cls.teacher_id, ...teacher, timezone: teacher.timezone || null })
    }
    for (const p of people) {
      if (p.phone) {
        sendWhatsApp('class_reminder_1h', p.phone, {
          student_name: (p.full_name || 'there').split(' ')[0],
          class_name:   cls.batch || cls.title,
          class_time:   `${classTimeLabel(cls, p.timezone)} (it is ON, please ignore the cancellation message)`,
          join_link:    cls.meet_link || 'Link to follow',
        }, { classId: cls.id, recipientId: p.id })
      }
      if (p.email) {
        sendEmail('class_scheduled', p.email, {
          name:        p.full_name,
          classTitle:  `BACK ON: ${cls.title}`,
          classDate:   fmtDate(cls.class_date),
          startTime:   cls.start_time || null,
          teacherName: cls.teacher_name || profile.full_name,
          zoomLink:    cls.meet_link || null,
          studentTZ:   p.timezone,
        })
      }
    }
    setMsg(`✓ "${cls.title}" is back on. ${people.filter(p => p.phone).length} people notified on WhatsApp.`)
    setBusyId(null)
    load()
  }

  if (loading) return <div style={{ textAlign:'center', padding:'2rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>Loading...</div>
  if (!cancelled.length) return <div style={{ textAlign:'center', padding:'3rem', color:'rgba(var(--fg-rgb),var(--a20))', fontSize:'13px' }}>{msg || 'No cancelled classes.'}</div>

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' })

  return (
    <div style={{ display:'grid', gap:'8px' }}>
      {msg && <div style={{ padding:'8px 14px', background: msg.startsWith('✓') ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', borderRadius:'9px', fontSize:'13px', color: msg.startsWith('✓') ? '#34d399' : '#f87171' }}>{msg}</div>}
      {cancelled.map(cls => (
        <div key={cls.id} style={{ display:'flex', alignItems:'center', gap:'12px', padding:'12px 16px', background:'rgba(239,68,68,0.05)', borderRadius:'12px', border:'0.5px solid rgba(239,68,68,0.15)' }}>
          <span style={{ fontSize:'18px' }}>🚫</span>
          <div style={{ flex:1 }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'rgba(var(--fg-rgb),var(--a60))', textDecoration:'line-through' }}>{cls.title}</div>
            <div style={{ fontSize:'11px', color:'rgba(var(--fg-rgb),var(--a30))', marginTop:'2px' }}>
              Was: {fmtDate(cls.class_date)} {cls.start_time && `· ${fmtTime(cls.start_time)}`}
              {cls.cancel_reason && <span> · Reason: {cls.cancel_reason}</span>}
            </div>
          </div>
          <span style={{ fontSize:'10px', color:'rgba(var(--fg-rgb),var(--a25))' }}>{cls.cancelled_at ? new Date(cls.cancelled_at).toLocaleDateString('en-IN') : ''}</span>
          {cls.class_date >= today && (
            <button onClick={() => undoCancel(cls)} disabled={busyId === cls.id} style={{ fontSize:'11px', fontWeight:700, padding:'6px 12px', borderRadius:'8px', background:'rgba(16,185,129,0.12)', color:'#34d399', border:'0.5px solid rgba(16,185,129,0.3)', cursor:'pointer', fontFamily:'inherit', flexShrink:0, opacity: busyId === cls.id ? 0.6 : 1 }}>
              {busyId === cls.id ? '⏳' : '↩ Undo cancel'}
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
